<template>
  <pf-data-list>
    <template v-for="row in items">
      <pf-data-list-item
        v-if="row.nome"
        :key="row.codice"
        :data-ouia-component-id="`DataListItem-${row.codice}`"
      >
        <pf-data-list-cell style="max-width: 465px">
          <pf-content>
            <h3>{{ row.nome }}</h3>
            <small>{{ $t("Codice prodotto") }}: {{ row.codice }}</small>
            <small>
              <i18n-t
                v-if="!row.group_code && row.durata != 'Una tantum'"
                keypath="Prodotto disponibile solo con fatturazione {0}"
                tag="i"
              >
                <template v-if="row.durata === 'Annuale'">
                  {{ $t("annuale") }}
                </template>
                <template v-else-if="row.durata === 'Mensile'">
                  {{ $t("mensile") }}
                </template>
              </i18n-t>
            </small>
          </pf-content>
        </pf-data-list-cell>

        <pf-data-list-cell>
          <product-details :product="row" />
        </pf-data-list-cell>

        <pf-data-list-action v-if="!hidePrice" class="prices">
          <pf-content>
            <small v-if="!alwaysFullPrice && row.prezzo > row.prezzo_scontato" class="full-price">
              <s>
                <template v-if="showQt && row.qt && row.qt > 1">{{ row.qt }} x</template>
                {{ $currency(row.prezzo, currency) }}
                <pf-tooltip v-if="login.is_reseller">
                  <template #content>
                    <slot name="field-help">
                      {{ $t("{0} Prezzo minimo di rivendita", [$currency(row.prezzo, currency)]) }}
                    </slot>
                  </template>
                  <help-icon /> </pf-tooltip
                >.
                {{ " " }}
                <pf-tooltip v-if="row.categoria === 'Internet Domain — Other TLD'">
                  <template #content>
                    <slot name="field-help">
                      {{
                        $t(
                          "In caso di domini classificati come premium, il costo di registrazione potrebbe essere superiore rispetto alle tariffe standard. In questi casi, la registrazione verrà preventivamente quotata e attivata solo previa conferma.",
                        )
                      }}
                    </slot>
                  </template>
                  <triangle-exclamation-icon />
                </pf-tooltip>
              </s>
            </small>
            <small v-else-if="showQt && row.qt && row.qt > 1" class="full-price">
              {{ row.qt }} x
              {{ $currency(alwaysFullPrice ? row.prezzo : row.prezzo_scontato, currency) }}
            </small>

            <p class="final-price">
              {{
                $currency(
                  (alwaysFullPrice ? row.prezzo : row.prezzo_scontato) * (row.qt ?? 1),
                  currency,
                )
              }}
            </p>

            <small v-if="!hidePlusVat"> + {{ $t("IVA") }} {{ getPeriodo(row.durata) }} </small>
            <small v-else>
              {{ $t(row.durata) }}
            </small>
            <small
              v-if="
                login.is_reseller &&
                row.prezzo_customer != undefined &&
                row.prezzo_customer !== row.prezzo
              "
            >
              {{ $t("Prezzo ai tuoi clienti") }}: {{ $currency(row.prezzo_customer, currency) }}
            </small>
          </pf-content>
        </pf-data-list-action>
        <pf-data-list-action v-else-if="hidePrice">
          <p class="free">{{ $t("GRATIS") }}</p>
        </pf-data-list-action>
      </pf-data-list-item>

      <pf-data-list-item v-else>
        <pf-data-list-cell>
          <triangle-exclamation-icon /> {{ $t("Codice prodotto {0} non trovato", [row.codice]) }}
        </pf-data-list-cell>
      </pf-data-list-item>
    </template>
  </pf-data-list>
</template>

<style scoped lang="scss">
.prices {
  text-align: center;
  min-width: 120px;

  small.full-price {
    margin-bottom: 0px;
  }

  p.final-price {
    font-size: var(--pf-t--global--font--size--600);
    margin-bottom: 0px;
  }
}
.free {
  font-size: var(--pf-t--global--font--size--400);
}
</style>

<script lang="ts" setup>
import { $t as _t } from "@/i18n";
import { type NormalizedCatalogProduct } from "../pages/catalog.vue";
import ProductDetails from "./product-details.vue";
import TriangleExclamationIcon from "@vue-patternfly/icons/triangle-exclamation-icon";
import { useLoginStore } from "@/store/login";
import { type PRODOTTIANAGRAFICA_DURATA } from "../common/consts";

defineProps<{
  items: NormalizedCatalogProduct[];
  currency: string;
  alwaysFullPrice?: boolean;
  hidePlusVat?: boolean;
  hidePrice?: boolean;
  showQt?: boolean;
}>();

const login = useLoginStore();

function getPeriodo(durata: (typeof PRODOTTIANAGRAFICA_DURATA)[number]) {
  let p = "";

  if (durata === "Mensile") {
    p = `/ ${_t("MESE")}`;
  } else if (durata === "Annuale") {
    p = `/ ${_t("ANNO")}`;
  } else {
    p = `/ ${_t("UNA TANTUM")}`;
  }

  return p;
}
</script>
